
import type { MouseEvent } from 'react';
import { isTauri } from '@/lib/utils/tauri';
import { useTauriTitleBar } from '@/contexts/TauriTitleBarContext';
import { createLogger } from '@/lib/logger';

const logger = createLogger('components/layout/TauriDragRegion');

/**
 * TauriDragRegion - A global drag region for the Tauri desktop app
 * 
 * This component provides a drag region at the top of the window for pages
 * that don't have the full TauriTitleBar (like login, auth callbacks, etc.)
 * 
 * When TauriTitleBar is active, this component does not render to avoid conflicts.
 */
export function TauriDragRegion() {
  const { isTitleBarActive } = useTauriTitleBar();

  // Programmatic window dragging using Tauri's startDragging API
  const handleMouseDown = async (e: MouseEvent) => {
    // Only start dragging on left mouse button
    if (e.button !== 0) return;

    const target = e.target as HTMLElement;

    // Don't drag if clicking on interactive elements
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input') ||
      target.closest('[role="button"]') ||
      target.closest('[data-no-drag]')
    ) {
      return;
    }

    try {
      const { getCurrentWindow } = await import('@tauri-apps/api/window');
      const window = getCurrentWindow();
      await window.startDragging();
    } catch (error) {
      logger.error('Failed to start window dragging:', error);
    }
  };

  // Only render for Tauri desktop app when TauriTitleBar is NOT active
  if (!isTauri() || isTitleBarActive) {
    return null;
  }

  return (
    <div
      className="fixed top-0 left-0 right-0 h-[52px] z-40 bg-transparent"
      data-tauri-drag-region
      onMouseDown={handleMouseDown}
      aria-hidden="true"
    />
  );
}
